import type { AxiosResponse } from 'axios';
import { api } from '@/lib/axios';
import type {
  FiscalizacionNotificationResult,
  MunicipalListDetail,
  MunicipalListPayload,
  MunicipalListSearchParams,
  MunicipalListSummary,
  Municipality,
  MunicipalityLocality,
  PaginatedResponse,
  PreRegisteredAccommodation,
  PreRegisteredAccommodationCreatePayload,
  PreRegisteredAccommodationExportParams,
  PreRegisteredAccommodationPayload,
  PreRegisteredAccommodationSearchParams,
  PreRegistrationStatus,
  PreRegistrationStatusChangePayload,
} from '../types/pre-registration.types';
import type { OfficialAccommodationMatch } from '../types/official-accommodation.types';

export const preRegistrationApi = {
  // Official REPAT registry: every word of `q` must match the name, file number or owner
  lookupOfficialAccommodations: async (
    q: string,
    limit: number
  ): Promise<OfficialAccommodationMatch[]> => {
    const { data } = await api.get<OfficialAccommodationMatch[]>('/api/accommodations/lookup', {
      params: { q, limit },
    });
    return data;
  },

  searchAccommodations: async (
    params: PreRegisteredAccommodationSearchParams
  ): Promise<PaginatedResponse<PreRegisteredAccommodation>> => {
    const { data } = await api.post<PaginatedResponse<PreRegisteredAccommodation>>(
      '/api/pre-registrations/accommodations/search',
      params
    );
    return data;
  },

  // Whole response: the hook needs Content-Disposition for the file name
  exportAccommodations: (params: PreRegisteredAccommodationExportParams): Promise<AxiosResponse<Blob>> =>
    api.post<Blob>('/api/pre-registrations/accommodations/export-excel', params, {
      responseType: 'blob',
    }),

  getStatuses: async (): Promise<PreRegistrationStatus[]> => {
    const { data } = await api.get<PreRegistrationStatus[]>(
      '/api/pre-registrations/accommodation-statuses'
    );
    return data;
  },

  searchMunicipalLists: async (
    params: MunicipalListSearchParams = {}
  ): Promise<MunicipalListSummary[]> => {
    const { data } = await api.post<MunicipalListSummary[]>(
      '/api/pre-registrations/municipal-lists/search',
      params
    );
    return data;
  },

  getMunicipalities: async (departmentId?: number): Promise<Municipality[]> => {
    const { data } = await api.get<Municipality[]>('/api/municipalities', {
      params: departmentId ? { departmentId } : undefined,
    });
    return data;
  },

  getLocalitiesByMunicipality: async (municipalityId: number): Promise<MunicipalityLocality[]> => {
    const { data } = await api.get<MunicipalityLocality[]>('/api/localities', {
      params: { municipalityId },
    });
    return data;
  },

  getMunicipalList: async (id: string): Promise<MunicipalListDetail> => {
    const { data } = await api.get<MunicipalListDetail>(
      `/api/pre-registrations/municipal-lists/${id}`
    );
    return data;
  },

  createMunicipalList: async (payload: MunicipalListPayload): Promise<MunicipalListDetail> => {
    const { data } = await api.post<MunicipalListDetail>(
      '/api/pre-registrations/municipal-lists',
      payload
    );
    return data;
  },

  updateMunicipalList: async (
    id: string,
    payload: MunicipalListPayload
  ): Promise<MunicipalListDetail> => {
    const { data } = await api.put<MunicipalListDetail>(
      `/api/pre-registrations/municipal-lists/${id}`,
      payload
    );
    return data;
  },

  deleteMunicipalList: async (id: string): Promise<void> => {
    await api.delete(`/api/pre-registrations/municipal-lists/${id}`);
  },

  notifyFiscalizacion: async (listId: string): Promise<FiscalizacionNotificationResult> => {
    const { data } = await api.post<FiscalizacionNotificationResult>(
      `/api/pre-registrations/municipal-lists/${listId}/notifications`
    );
    return data;
  },

  createAccommodation: async (
    listId: string,
    payload: PreRegisteredAccommodationCreatePayload
  ): Promise<PreRegisteredAccommodation> => {
    const { data } = await api.post<PreRegisteredAccommodation>(
      `/api/pre-registrations/municipal-lists/${listId}/accommodations`,
      payload
    );
    return data;
  },

  updateAccommodation: async (
    id: string,
    payload: PreRegisteredAccommodationPayload
  ): Promise<PreRegisteredAccommodation> => {
    const { data } = await api.put<PreRegisteredAccommodation>(
      `/api/pre-registrations/accommodations/${id}`,
      payload
    );
    return data;
  },

  changeAccommodationStatus: async (
    id: string,
    payload: PreRegistrationStatusChangePayload
  ): Promise<PreRegisteredAccommodation> => {
    const { data } = await api.patch<PreRegisteredAccommodation>(
      `/api/pre-registrations/accommodations/${id}/status`,
      payload
    );
    return data;
  },
};
